import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { publicApi } from "@/lib/api";
import { PUBLIC } from "@/constants/testIds";
import { useSiteTheme } from "@/components/public/useSiteTheme";
import { ExternalLink } from "lucide-react";
import SeoHead, { buildNewsArticleJsonLd } from "@/components/public/SeoHead";
import AdSlot from "@/components/public/AdSlot";

export default function PublicArticle() {
  const { slug, articleSlug } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    publicApi.get(`/public/sites/${slug}/article/${articleSlug}`).then((r) => setData(r.data)).catch((e) => setErr(e.response?.data?.detail || e.message));
  }, [slug, articleSlug]);
  useSiteTheme(data?.site);

  if (err) return <div className="public-site min-h-screen flex items-center justify-center">Not found.</div>;
  if (!data) return <div className="public-site min-h-screen flex items-center justify-center"><div className="opacity-60 mono">Loading…</div></div>;

  const { site, article, related } = data;
  const canonical = site.domain
    ? `https://${site.domain}/article/${article.slug}`
    : window.location.href;
  const jsonLd = buildNewsArticleJsonLd({ site, article, canonical });
  const bodyParas = (article.body_markdown || "").split(/\n\n+/);
  const midpoint = Math.max(1, Math.floor(bodyParas.length / 2));

  return (
    <div className="public-site">
      <SeoHead
        site={site}
        title={article.seo_title || article.title}
        description={article.seo_description || article.summary}
        image={article.media?.social || article.image_url}
        type="article"
        jsonLd={jsonLd}
      />
      <nav className="border-b" style={{ borderColor: "var(--site-rule)" }}>
        <div className="max-w-6xl mx-auto px-4 flex items-center gap-6 py-3 text-sm">
          <Link to={`/site/${site.slug}`} className="display text-lg font-bold">{site.name}</Link>
          <div className="text-[11px] uppercase tracking-widest opacity-60">{article.category_slug}</div>
        </div>
      </nav>

      <article className="max-w-3xl mx-auto px-4 py-10">
        <div className="text-[11px] uppercase tracking-widest opacity-60">{article.category_slug}</div>
        <h1 className="display text-3xl md:text-5xl font-black tracking-tight mt-2 leading-[1.05]" style={{ color: "var(--site-text)" }}>
          {article.title}
        </h1>
        {article.summary && (
          <div className="text-xl opacity-80 leading-relaxed mt-4">{article.summary}</div>
        )}
        {article.image_url && (
          <img src={article.image_url} alt="" className="w-full aspect-[16/9] object-cover mt-6" loading="lazy" />
        )}

        <AdSlot site={site} placement="header" />

        <div className="mt-8 prose-content space-y-5 text-base leading-[1.75]">
          {bodyParas.map((p, i) => (
            <React.Fragment key={i}>
              <p>{p}</p>
              {i === midpoint - 1 && <AdSlot site={site} placement="in_article" />}
            </React.Fragment>
          ))}
        </div>
        <div className="mt-10 border-t pt-6" style={{ borderColor: "var(--site-rule)" }}>
          <a href={article.source_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm font-semibold" style={{ color: "var(--site-accent)" }}>
            Continue reading at {article.source_name} <ExternalLink size={14} />
          </a>
        </div>
      </article>

      {related?.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 pb-12">
          <div className="text-[11px] uppercase tracking-widest opacity-60 mb-4">More from {site.name}</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {related.map((r) => (
              <Link key={r.id} to={`/site/${site.slug}/article/${r.slug}`} className="block group">
                {r.image_url && <img src={r.image_url} alt="" className="w-full aspect-[4/3] object-cover mb-3" loading="lazy" />}
                <div className="display text-lg font-bold leading-tight group-hover:opacity-80">{r.title}</div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <AdSlot site={site} placement="footer" />
    </div>
  );
}
